import {
  IonContent,
  IonPage,
  IonSkeletonText,
  IonToggle,
  type ToggleCustomEvent,
} from '@ionic/react';
import { useState } from 'react';
import { Redirect } from 'react-router-dom';

import type { Company } from '~/api/auth';
import type { CompanyDocument, DocumentSection, FetchDocumentsParams } from '~/api/documents';
import { useCompany } from '~/api/hooks/useCompany';
import { useDocumentsQuery, useSectionsQuery } from '~/api/hooks/useDocuments';
import { CompanySwitcher } from '~/components/CompanySwitcher';
import { useTenant } from '~/components/TenantGate';
import { Icon } from '~/components/ui/Icon';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { DocumentoSheet } from '~/pages/documentos/DocumentoSheet';
import { DocumentRow, officeShortName } from '~/pages/documentos/PostCard';
import { ROUTES } from '~/routes';

type SectionCode = DocumentSection['section'];
type TypeFolder = DocumentSection['types'][number];
type TypeCode = TypeFolder['type'];
type PeriodFolder = TypeFolder['periods'][number];

/** O teto da página na API (`max_page_size`): a pasta mostra os cem documentos mais recentes. */
const DOCUMENTS_LIMIT = 100;
const WITHOUT_PERIOD: TypeFolder['period_kind'] = 'NONE';
const SKELETON_ROWS = [0, 1, 2];
const TRAIL_SEPARATOR = ' · ';

/** A pasta aberta, guardada pelos códigos: os rótulos e as contagens vêm sempre da consulta. */
interface FolderPath {
  section: SectionCode | null;
  type: TypeCode | null;
  period: string | null;
}

const TOP_FOLDER: FolderPath = { section: null, type: null, period: null };

type FolderView =
  | { level: 'sections'; sections: DocumentSection[] }
  | { level: 'types'; section: DocumentSection }
  | { level: 'periods'; section: DocumentSection; folder: TypeFolder }
  | {
      level: 'documents';
      section: DocumentSection;
      folder: TypeFolder;
      /** `null` no tipo sem período, cuja lista abre logo depois do tipo. */
      period: PeriodFolder | null;
    };

/**
 * O que a pasta aberta mostra. Uma pasta que sumiu da consulta (a empresa trocou, o escritório
 * excluiu o último documento dela) volta para a mais próxima que ainda existe.
 */
function folderView(sections: DocumentSection[], path: FolderPath): FolderView {
  const section = sections.find((candidate) => candidate.section === path.section);
  if (!section) return { level: 'sections', sections };
  const folder = section.types.find((candidate) => candidate.type === path.type);
  if (!folder) return { level: 'types', section };
  if (folder.period_kind === WITHOUT_PERIOD) {
    return { level: 'documents', section, folder, period: null };
  }
  const period = folder.periods.find((candidate) => candidate.period === path.period);
  if (!period) return { level: 'periods', section, folder };
  return { level: 'documents', section, folder, period };
}

/** A pasta de cima; `null` no topo, de onde o voltar leva ao feed. */
function parentOf(view: FolderView): FolderPath | null {
  if (view.level === 'sections') return null;
  if (view.level === 'types') return TOP_FOLDER;
  const sectionOnly: FolderPath = { ...TOP_FOLDER, section: view.section.section };
  if (view.level === 'periods' || view.period === null) return sectionOnly;
  return { ...sectionOnly, type: view.folder.type };
}

function titleOf(view: FolderView): string {
  if (view.level === 'sections') return 'Pastas';
  if (view.level === 'types') return view.section.label;
  if (view.level === 'periods' || view.period === null) return view.folder.label;
  return `${view.folder.label} ${view.period.label}`;
}

/** O caminho até a pasta, acima do título; o topo não tem. */
function trailOf(view: FolderView): string | null {
  if (view.level === 'sections') return null;
  if (view.level === 'types') return 'Pastas';
  if (view.level === 'periods' || view.period === null) return view.section.label;
  return [view.section.label, view.folder.label].join(TRAIL_SEPARATOR);
}

function documentsParams(
  folder: TypeFolder,
  period: PeriodFolder | null,
  withHistory: boolean,
): FetchDocumentsParams {
  const params: FetchDocumentsParams = { type: folder.type, limit: DOCUMENTS_LIMIT };
  if (period) params.period = period.period;
  if (withHistory) params.superseded = true;
  return params;
}

function documentCountLabel(count: number): string {
  return count === 1 ? '1 documento' : `${count} documentos`;
}

/**
 * `/documentos/pastas`: os documentos atuais da empresa em operação por seção, tipo e período,
 * com a contagem de cada pasta. A navegação entre as pastas é estado da tela, sem rotas
 * aninhadas: o voltar sobe uma pasta e, no topo, volta ao feed. Fica atrás de `RequireModule`
 * (`documents` `READ`), como o feed.
 */
export function Pastas(): JSX.Element {
  const { selected } = useCompany();

  return (
    <IonPage>
      <IonContent fullscreen>
        {selected ? <CompanyFolders company={selected} /> : <Redirect to={ROUTES.dashboard} />}
      </IonContent>
    </IonPage>
  );
}

function CompanyFolders({ company }: { company: Company }): JSX.Element {
  const nav = useAppNavigation();
  const tenant = useTenant();
  const sections = useSectionsQuery(company.id);
  const [path, setPath] = useState<FolderPath>(TOP_FOLDER);
  const [withHistory, setWithHistory] = useState(false);
  const [openedDocument, setOpenedDocument] = useState<CompanyDocument | null>(null);
  const view = folderView(sections.data ?? [], path);
  const parent = parentOf(view);
  const trail = trailOf(view);

  return (
    <div className="rx-container rx-screen pb-7">
      <div className="px-6 pt-3">
        <button
          type="button"
          className="rx-iconbtn"
          onClick={() => (parent ? setPath(parent) : nav.replace(ROUTES.documentos))}
          aria-label="Voltar"
        >
          <Icon name="back" size={18} />
        </button>
        {trail && (
          <p className="mb-0 mt-3.5 text-overline font-semibold uppercase text-ink-subtle">
            {trail}
          </p>
        )}
        <h1
          className={`mb-0 ${trail ? 'mt-1' : 'mt-3.5'} text-[30px] font-medium tracking-display-tight text-ink`}
        >
          {titleOf(view)}
        </h1>
        {view.level === 'sections' && (
          <div className="mt-2">
            <CompanySwitcher compact />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 px-6 pt-5">
        {sections.isPending ? (
          <RowsSkeleton label="Carregando as pastas" />
        ) : (
          <FolderContent
            view={view}
            clientId={company.id}
            officeName={officeShortName(tenant)}
            withHistory={withHistory}
            openedDocumentId={openedDocument?.id ?? null}
            onOpenFolder={setPath}
            onToggleHistory={setWithHistory}
            onOpenDocument={setOpenedDocument}
          />
        )}
      </div>

      <DocumentoSheet
        companyDocument={openedDocument}
        clientId={company.id}
        onClose={() => setOpenedDocument(null)}
      />
    </div>
  );
}

interface FolderContentProps {
  view: FolderView;
  clientId: number;
  officeName: string;
  withHistory: boolean;
  openedDocumentId: string | null;
  onOpenFolder(path: FolderPath): void;
  onToggleHistory(withHistory: boolean): void;
  onOpenDocument(companyDocument: CompanyDocument): void;
}

function FolderContent({
  view,
  clientId,
  officeName,
  withHistory,
  openedDocumentId,
  onOpenFolder,
  onToggleHistory,
  onOpenDocument,
}: FolderContentProps): JSX.Element {
  if (view.level === 'sections') {
    if (view.sections.length === 0) return <EmptyFolders officeName={officeName} />;
    return (
      <FolderList>
        {view.sections.map((section) => (
          <FolderButton
            key={section.section}
            label={section.label}
            count={section.count}
            onOpen={() => onOpenFolder({ ...TOP_FOLDER, section: section.section })}
          />
        ))}
      </FolderList>
    );
  }

  if (view.level === 'types') {
    const section = view.section.section;
    return (
      <FolderList>
        {view.section.types.map((folder) => (
          <FolderButton
            key={folder.type}
            label={folder.label}
            count={folder.count}
            onOpen={() => onOpenFolder({ section, type: folder.type, period: null })}
          />
        ))}
      </FolderList>
    );
  }

  if (view.level === 'periods') {
    const section = view.section.section;
    const type = view.folder.type;
    return (
      <FolderList>
        {view.folder.periods.map((period) => (
          <FolderButton
            key={period.period}
            label={period.label}
            count={period.count}
            onOpen={() => onOpenFolder({ section, type, period: period.period })}
          />
        ))}
      </FolderList>
    );
  }

  return (
    <DocumentsFolder
      clientId={clientId}
      params={documentsParams(view.folder, view.period, withHistory)}
      withHistory={withHistory}
      openedDocumentId={openedDocumentId}
      onToggleHistory={onToggleHistory}
      onOpenDocument={onOpenDocument}
    />
  );
}

function FolderList({ children }: { children: JSX.Element[] }): JSX.Element {
  return <ul className="m-0 flex list-none flex-col gap-2 p-0">{children}</ul>;
}

interface FolderButtonProps {
  label: string;
  count: number;
  onOpen(): void;
}

function FolderButton({ label, count, onOpen }: FolderButtonProps): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="rx-card flex w-full cursor-pointer items-center gap-3 text-left font-[inherit]"
      >
        <span aria-hidden="true" className="text-brand">
          <Icon name="folder" size={18} />
        </span>
        <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-ink">{label}</span>
        <span className="text-[12px] text-ink-subtle">{documentCountLabel(count)}</span>
        <span aria-hidden="true">
          <Icon name="chevron" size={14} color="var(--rx-text-3)" />
        </span>
      </button>
    </li>
  );
}

interface DocumentsFolderProps {
  clientId: number;
  params: FetchDocumentsParams;
  withHistory: boolean;
  openedDocumentId: string | null;
  onToggleHistory(withHistory: boolean): void;
  onOpenDocument(companyDocument: CompanyDocument): void;
}

/** Os documentos da pasta: os atuais e, com "Mostrar histórico", também os substituídos. */
function DocumentsFolder({
  clientId,
  params,
  withHistory,
  openedDocumentId,
  onToggleHistory,
  onOpenDocument,
}: DocumentsFolderProps): JSX.Element {
  const documents = useDocumentsQuery(clientId, params);
  const rows = documents.data?.results ?? [];

  return (
    <>
      <IonToggle
        className="rx-toggle"
        checked={withHistory}
        onIonChange={(event: ToggleCustomEvent) => onToggleHistory(event.detail.checked)}
        justify="space-between"
      >
        Mostrar histórico
      </IonToggle>

      {documents.isPending ? (
        <RowsSkeleton label="Carregando os documentos" />
      ) : rows.length === 0 ? (
        <p className="m-0 text-[13px] text-ink-muted">Nenhum documento nesta pasta.</p>
      ) : (
        <div className="rx-card">
          <ul className="m-0 list-none p-0">
            {rows.map((companyDocument, index) => (
              <li
                key={companyDocument.id}
                style={{ borderTop: index === 0 ? 'none' : '0.5px solid var(--rx-border)' }}
              >
                <DocumentRow
                  companyDocument={companyDocument}
                  opened={companyDocument.id === openedDocumentId}
                  onOpen={onOpenDocument}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {documents.data?.next && (
        <p className="m-0 text-[12px] text-ink-subtle">
          Mostrando os {DOCUMENTS_LIMIT} documentos mais recentes.
        </p>
      )}
    </>
  );
}

function RowsSkeleton({ label }: { label: string }): JSX.Element {
  return (
    <output aria-label={label} className="flex flex-col gap-2">
      {SKELETON_ROWS.map((index) => (
        <div key={index} className="rx-card">
          <IonSkeletonText animated style={{ width: '60%', height: 14 }} />
        </div>
      ))}
    </output>
  );
}

function EmptyFolders({ officeName }: { officeName: string }): JSX.Element {
  return (
    <div className="rx-card text-center" style={{ padding: '28px 16px' }}>
      <span
        aria-hidden="true"
        className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-brand-tint text-brand"
      >
        <Icon name="folder" size={22} />
      </span>
      <p className="mb-0 mt-3.5 text-overline font-semibold uppercase text-ink-subtle">
        Nenhum documento ainda
      </p>
      <p className="mx-auto mb-0 mt-2 max-w-[32ch] text-[13px] text-ink-muted">
        Quando {officeName} publicar um documento desta empresa, ele aparece aqui.
      </p>
    </div>
  );
}
